import { ReactElement } from 'react';
import { Field, useFormikContext } from 'formik';
import { iCourtFormValues } from '@services/CourtService';
import { INVALID_NUMERIC_FORMAT } from '@services/ErrorMessages';
import { TitleOptionsList } from '@constants';
import { AddressField, ContactInputWithExperian, DateInput, DropdownMenu, EmailFieldWithExperian, LicenceField, TextInput, TileRadio } from '@components';
import { tile } from '@interfaces/interfaces';
import { FormType } from '@constants/forms';

const { REACT_APP_SITECORE_API_ENDPOINT: API_URL } = process.env;

function CompanyInformation(): ReactElement {
  const { values, touched }: { values: iCourtFormValues; touched: any } = useFormikContext();
  const formikContext = useFormikContext();
  const Abn = (): ReactElement => (
    <>
      <div>ABN</div>
    </>
  );

  const Acn = (): ReactElement => (
    <>
      <div>ACN</div>
    </>
  );

  const ABNACN_TILE_DATA: tile[] = [
    {
      body: <Abn />,
      value: 'ABN',
    },
    {
      body: <Acn />,
      value: 'ACN',
    },
  ];

  const PostalAddress = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NotPostalAddress = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const POSTAL_TILE_DATA: tile[] = [
    {
      body: <PostalAddress />,
      value: 'true',
    },
    {
      body: <NotPostalAddress />,
      value: 'false',
    },
  ];

  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];

  const validateAbnAsync = (value: string) => {
    const url = `${API_URL}sitecore/PartialForms/ABNLookUp`;
    const payload = {
      InputNumber: value,
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.text())
      .then((responseBody) => {
        if (responseBody === 'False') {
          return 'Please enter a valid ABN';
        }
        return '';
      })
      .catch((error) => error);
  };

  const validateAcnAsync = (value: string) => {
    const url = `${API_URL}sitecore/PartialForms/ACNLookUp`;
    const payload = {
      InputNumber: value,
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.text())
      .then((responseBody) => {
        if (responseBody === 'False') {
          return 'Please enter a valid ACN';
        }
        return '';
      })
      .catch((error) => error);
  };
  if (values.NoEmail) {
    values.EMAIL_TO = 'false';
  }
  return (
    <>
      <div className="vfH2">Your contact information</div>
      <Field component={DropdownMenu} name="TitleMrMrsMsMissOther" title="Title" isMandatory displayText={TitleOptionsList} defaultOption />
      <Field component={TextInput} name="GivenNames" title="Given name(s)" isMandatory />
      <Field component={TextInput} name="FamilyName" title="Family name" isMandatory />
      <Field
        component={DropdownMenu}
        name="PositionOfThePersonWithinTheCompany"
        title="Position in the company"
        isMandatory
        displayText={[
          { value: 'DIRECTOR', display: 'Director' },
          { value: 'SECRETARY', display: 'Secretary' },
          { value: 'RECEIVER', display: 'Receiver' },
          { value: 'RECEIVERANDMANAGER', display: 'Receiver and manager' },
          { value: 'OFFICIALMANAGER', display: 'Official manager' },
          { value: 'LIQUIDATOR', display: 'Liquidator' },
          { value: 'TRUSTEE', display: 'Trustee' },
          { value: 'OTHER', display: 'Other' },
        ]}
        defaultOption
      />
      {values.PositionOfThePersonWithinTheCompany === 'OTHER' && <Field component={TextInput} name="OtherPosition" title="Please describe" isMandatory />}
      <Field name="DOB" component={DateInput} title="Date of birth" isMandatory maxDate={new Date()} />
      <LicenceField
        title="Driver licence number"
        name="DRIVER_LICENCE"
        nameStateOfIssue="LicenceState"
        nameCountryOfIssue="StatecountryOfIssue"
        nameNoDriverLicence="NoDriverLicence"
        isMandatory
      />
      <div>
        <span className="vfH3">Company details</span>
        <Field component={TextInput} name="CompanyName" title="Company name" isMandatory />
        <Field
          component={TileRadio}
          name="AbnacnType"
          title="Please enter your ABN or ACN below"
          tileData={ABNACN_TILE_DATA}
          orientationRow
          isMandatory
          tileChanged={() => {
            formikContext.setFieldValue('AbnNo', '');
            formikContext.setFieldTouched('AbnNo', false);
            formikContext.setFieldError('AbnNo', INVALID_NUMERIC_FORMAT);
            formikContext.setFieldValue('AcnNo', '');
            formikContext.setFieldTouched('AcnNo', false);
            formikContext.setFieldError('AcnNo', INVALID_NUMERIC_FORMAT);
          }}
        />
        {values.AbnacnType === 'ABN' && <Field component={TextInput} name="AbnNo" title="ABN" isMandatory validate={values.AbnNo && touched.AbnNo ? validateAbnAsync : null} />}
        {values.AbnacnType === 'ACN' && <Field component={TextInput} name="AcnNo" title="ACN" isMandatory validate={values.AcnNo && touched.AcnNo ? validateAcnAsync : null} />}
        <AddressField name="CompanyAddress" title="Company's street address (Court summons will be sent here)" manualAddressText="Enter the address manually" isMandatory />
        <Field
          name="HasDifferentCompanyAddress"
          component={TileRadio}
          tileData={POSTAL_TILE_DATA}
          orientationRow
          mobileCollapseRow={false}
          title="Is the postal address different to your company's street address?"
          isMandatory
        />
        {values.HasDifferentCompanyAddress === 'true' && (
          <AddressField name="CompanyPostalAddress" title="Postal address" manualAddressText="Enter the address manually" isMandatory />
        )}
        <Field component={ContactInputWithExperian} name="CourtContactNumber" title="Contact phone number" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={FormType.COURT} numberTypeMandatory={false} secondaryContact warningMessage="CourtContactNumberWarning" warningMessageSec="CourtContactNumberWarningSec" />
        <Field component={EmailFieldWithExperian} name="Email" title="Email" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={FormType.COURT} noEmailField="NoEmail" currentSP="Select if you do not have an email address" warningMessage="EmailWarning" />
        {!values.NoEmail && (
          <Field
            name="EMAIL_TO"
            component={TileRadio}
            tileData={TILE_DATA}
            title="Would you like to receive a copy of the completed application at the email address provided?"
            isMandatory
            orientationRow
          />
        )}
      </div>
    </>
  );
}

export default CompanyInformation;
